import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Select,
    MenuItem,
    Button,
    TextField,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    FormControl,
    InputLabel
} from '@mui/material';

import { useUser } from '../components/UserContext';
import { useApiClient } from '../components/useApiClient';
import useAuthToken from '../functions/useAuthToken';
import { ClientUser, UserRole } from "../models/UserProfile";
import type { UserInviteRequest } from "../models/UserInvite";


const UserManagementPage: React.FC = () => {
    const { activeClientId, invalidateUser, isAdmin, isOwner } = useUser();
    const getAccessToken = useAuthToken();
    const { get, put, postJson, delete: del } = useApiClient();

    const [users, setUsers] = useState<ClientUser[]>([]);
    const [roles, setRoles] = useState<UserRole[]>([]);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteRoleId, setInviteRoleId] = useState<number | null>(null);
    const [status, setStatus] = useState('');
    const canEditUsers = isAdmin || isOwner;

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    useEffect(() => {

        if (!clientIdRef.current || !canEditUsers) return;

        const fetchData = async () => {

            try {
                const accessToken = await getAccessToken();
                if (!accessToken) return;

                const roleList = await get<UserRole[]>('user/roles',accessToken);
                setRoles(roleList);
                const userList = await get<ClientUser[]>('user/list', accessToken);
                setUsers(userList);
            } catch (err) {

            }
        };

        fetchData();

    }, [clientIdRef.current]);

    const handleRoleChange = async (userId: number, newRoleId: number) => {
        if (!clientIdRef.current) return;

        const accessToken = await getAccessToken();
        if (!accessToken) return;

        try {
            const formData = new FormData();
            formData.append("UserId", userId.toString());
            formData.append("ClientId", clientIdRef.current.toString());
            formData.append("UserRoleId", newRoleId.toString());
            formData.append("IsActive", true.toString());

            await put(`user`, accessToken, formData);
            setStatus('User role updated');
            const updated = await get<ClientUser[]>('user/list', accessToken);
            setUsers(updated);
        } catch (err) {
            setStatus('Error updating user');
        }
    };

    const handleInvite = async () => {
        if (!clientIdRef.current || !inviteEmail || !inviteRoleId) return;

        const accessToken = await getAccessToken();
        if (!accessToken) return;

        try {

            const dto: UserInviteRequest = {
                email: inviteEmail,
                clientId: activeClientId ?? 0,
                roleId: inviteRoleId,
            }

            var result = await postJson<string>("invitations", accessToken, JSON.stringify(dto));

            setInviteEmail('');
            setInviteRoleId(null);
            setStatus('Invitation sent');

            //await refreshUser();
            invalidateUser();

        } catch (err) {
            console.error('Failed to send invite', err);
            setStatus('Failed to send invite');
        }
    };

    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', mt: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Typography variant="h5" gutterBottom>User Access Management</Typography>

                {canEditUsers ?  (
                    <>
                    <Typography variant="subtitle1" sx={{ mt: 2 }}>Existing Users</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Email</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Role</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {users.map((u) => (
                                <TableRow key={u.userId}>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>{u.displayName}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={u.roleId}
                                            onChange={(e) => handleRoleChange(u.userId, Number(e.target.value))}
                                            size="small"
                                        >
                                            {roles.map((r) => (
                                                <MenuItem key={r.id} value={r.id}>
                                                    {r.role}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Typography variant="subtitle1" sx={{ mt: 4 }}>Invite New User</Typography>
                    <TextField
                        label="Email"
                        fullWidth
                        margin="normal"
                        size="small"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                    />
                    <FormControl fullWidth size="small" margin="normal">
                        <InputLabel id="invite-role-label">Role</InputLabel>
                        <Select
                            labelId="invite-role-label"
                            value={inviteRoleId ?? ''}
                            onChange={(e) => setInviteRoleId(Number(e.target.value))}
                            label="Role"
                        >
                            {roles.map((r) => (
                                <MenuItem key={r.id} value={r.id}>{r.role}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button variant="outlined" onClick={handleInvite} sx={{ mt: 2 }}>
                        Send Invite
                    </Button>

                        {status && <Typography sx={{ mt: 2 }}>{status}</Typography>}
                    </>) : (
                        <>
                            <p>You are not authorised to manage users for this client/collection.</p>
                        </>
                    )}
            </Paper>
        </Box>
    );
};

export default UserManagementPage;
